import { getClient } from '../core/global-config.js';
import { createAndWaitTask } from '../core/index.js';

export interface TencentMpsSuperResolutionParams {
  /**
   * 输入视频URL
   */
  input_url: string;
  /**
   * 输入类型，默认为URL
   * @default "URL"
   */
  input_type?: string;
  /**
   * 输出存储桶名称
   * @default "top-rank-cos-1350346443"
   */
  output_bucket?: string;
  /**
   * 输出存储区域
   * @default "na-siliconvalley"
   */
  output_region?: string;
  /**
   * 输出存储类型
   * @default "COS"
   */
  output_storage_type?: string;
  /**
   * 输出目录
   * @default "/super_resolution"
   */
  output_dir?: string;
  /**
   * 转码模板定义，可选值：1560240(720p)、1581780(1080p)、其他(1440p) 也可输入"720p"、"1080p"、"2k"
   * @default 1560242
   */
  definition?: number;
  short?: number;
  /**
   * 会话ID
   */
  session_id?: string;
  /**
   * 会话上下文
   */
  session_context?: string;
  /**
   * 任务通知配置
   */
  task_notify_config?: {
  };
  /**
   * 任务优先级
   * @default 0
   */
  task_priority?: number;
}

export interface TencentMpsSuperResolutionResource {
  type: string;
  url: string;
  size?: number;
  jobId?: string;
  [key: string]: any;
}

/**
 * tencent_mps_super_resolution
 *
 * <p>Tencent MPS Super Resolution implementation.</p>
 * <p>API Documentation: https://cloud.tencent.com/document/product/862/37578</p>
 * 
 *
 * @example
 * ```typescript
 * import { initSeacloud, tencentMpsSuperResolution } from 'seacloud-sdk';
 *
 * initSeacloud('your-api-key');
 *
 * const result = await tencentMpsSuperResolution({
 *   // 参数见 TencentMpsSuperResolutionParams 接口定义
 * });
 *
 * console.log(result); // 返回生成的资源列表
 * ```
 *
 * @param params 请求参数，详见 TencentMpsSuperResolutionParams 接口定义
 * @returns 生成的资源列表
 */
export async function tencentMpsSuperResolution(
  params: TencentMpsSuperResolutionParams,
): Promise<TencentMpsSuperResolutionResource[]> {
  const client = await getClient();

  const pollingOptions = {
    intervalMs: 3000,
    maxAttempts: 60,
  };

  const result = await createAndWaitTask(
    client,
    '/model/v1/generation',
    {
      model: 'tencent_mps_super_resolution',
      input: [{ params }],
    },
    pollingOptions,
  );

  const resources: TencentMpsSuperResolutionResource[] = [];

  if (result.output) {
    for (const item of result.output) {
      if (item.content) {
        for (const resource of item.content) {
          resources.push({
            type: resource.type || 'unknown',
            url: resource.url || '',
            size: resource.size,
            jobId: resource.jobId,
            ...resource,
          });
        }
      }
    }
  }

  return resources;
}
